import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'umi';
import { Empty, Skeleton, Typography } from 'antd';

import { F } from '@/utils/intl';
import { useHasAccess } from '@/utils/hooks';
import * as SkillSelectors from '@/selectors/skillSelectors';
import { SKILLS_ACCESS } from './constants';
import SkillForm from './SkillForm';
import SkillRow from './SkillRow';
import styles from './index.module.less';

/**
 * The partner's agent skills: label + the slug booking APIs request them by.
 * Read-only without skills_manage. Shown in the skills drawer and on the data
 * entry management page; loads the list when it mounts.
 */
const SkillsManager: React.FC = () => {
  const dispatch = useDispatch();
  const skills = useSelector(SkillSelectors.selectSkills);
  const initialized = useSelector(SkillSelectors.selectSkillsInitialized);
  const canManage = useHasAccess(SKILLS_ACCESS.manage);
  const [editingId, setEditingId] = useState<string | null>(null);

  const refresh = useCallback(() => dispatch({ type: 'skills/fetch' }), [dispatch]);
  const stopEditing = useCallback(() => setEditingId(null), []);
  const handleSaved = useCallback(() => {
    setEditingId(null);
    refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, []);

  return (
    <>
      <Typography.Paragraph type="secondary">
        <F
          id="component.SkillsDrawer.intro"
          defaultMessage="A booking can ask for skills: of the free agents, the one matching most of them is booked. Integrations refer to a skill by its slug, which is generated from the label unless you set one."
        />
      </Typography.Paragraph>
      {canManage && <SkillForm onSaved={refresh} />}
      {!initialized ? (
        <Skeleton active title={false} paragraph={{ rows: 4 }} />
      ) : skills.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <ul className={styles.list}>
          {skills.map((skill) =>
            skill.id === editingId ? (
              <li key={skill.id} className={styles.editingRow}>
                <SkillForm skill={skill} onSaved={handleSaved} onCancel={stopEditing} />
              </li>
            ) : (
              <SkillRow
                key={skill.id}
                skill={skill}
                onEdit={canManage ? setEditingId : undefined}
                onDeleted={refresh}
              />
            ),
          )}
        </ul>
      )}
    </>
  );
};

export default React.memo(SkillsManager);
